import {
  buildConnectionsBhSankeyLinks,
  type ConnectionsBhSankeyNode,
} from '@act/shared/data-transfer-objects';
import { sankey, sankeyLinkHorizontal, sankeyJustify } from 'd3-sankey';

type SankeyLayoutNode = ConnectionsBhSankeyNode & {
  color: string;
  labelPos: 'left' | 'above' | 'below' | 'right';
  label: string;
  x0?: number;
  x1?: number;
  y0?: number;
  y1?: number;
};

type SankeyLinkDatum = {
  source: number;
  target: number;
  value: number;
  color: string;
  width?: number;
};

/** Visual style per BH Sankey node id (Studio design tokens). */
export const BH_SANKEY_NODE_STYLE: Record<
  string,
  { color: string; labelPos: 'left' | 'above' | 'below' | 'right' }
> = {
  connected: { color: '#81CDC3', labelPos: 'left' },
  inNetwork: { color: '#614189', labelPos: 'above' },
  reema: { color: '#81CDC3', labelPos: 'below' },
  currently: { color: '#158C7C', labelPos: 'right' },
  noLonger: { color: '#81CDC3', labelPos: 'right' },
};

/** Static mock nodes for HTML export decor (legacy Studio sample). */
const BH_SANKEY_STATIC_NODES: ConnectionsBhSankeyNode[] = [
  {
    id: 'connected',
    label: 'Members connected to behavioral healthcare',
    value: 376,
  },
  {
    id: 'inNetwork',
    label: 'Members seen by an in-network clinician',
    value: 193,
  },
  { id: 'reema', label: 'Members seen by Reema clinician', value: 183 },
  {
    id: 'currently',
    label: "Members currently in Reema's care",
    value: 67,
  },
  {
    id: 'noLonger',
    label: "Members no longer in Reema's care",
    value: 116,
  },
];

const WIDTH = 1080;
const HEIGHT = 520;
const PAD_TOP = 110;
const PAD_BOTTOM = 130;
const PAD_LEFT = 280;
const PAD_RIGHT = 300;

const OVERLINE_FONT = 11;
const OVERLINE_LH = 15;
const OVERLINE_FILL = '#0B1A2A';
const NUM_FONT = 36;
const GAP = 6;
const MAX_OVERLINE_CHARS = 22;
const LABEL_MIN_GAP = 12;
const LABEL_VIEW_INSET = 8;
const OVERLINE_CHAR_WIDTH = OVERLINE_FONT * 0.85;
const NUM_CHAR_WIDTH = NUM_FONT * 0.62;

type SankeyLabelAnchor = 'start' | 'end' | 'middle';

interface SankeyLabelBlock {
  x: number;
  x0: number;
  x1: number;
  yTop: number;
  yBottom: number;
  anchor: SankeyLabelAnchor;
  overlineFirstY: number;
  numY: number;
  lines: string[];
  numStr: string;
  numColor: string;
}

const escapeXml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const estimateLabelWidth = (lines: string[], numStr: string): number =>
  Math.max(
    Math.max(...lines.map(line => line.length), 1) * OVERLINE_CHAR_WIDTH,
    Math.max(numStr.length, 1) * NUM_CHAR_WIDTH,
  );

const horizontalExtent = (
  x: number,
  anchor: SankeyLabelAnchor,
  width: number,
): { x0: number; x1: number } => {
  if (anchor === 'end') {
    return { x0: x - width, x1: x };
  }
  if (anchor === 'middle') {
    return { x0: x - width / 2, x1: x + width / 2 };
  }
  return { x0: x, x1: x + width };
};

const verticalMetrics = (
  overlineFirstY: number,
  lineCount: number,
): { numY: number; yTop: number; yBottom: number } => {
  const numY =
    overlineFirstY + (lineCount - 1) * OVERLINE_LH + GAP + NUM_FONT;
  return {
    numY,
    yTop: overlineFirstY - OVERLINE_FONT,
    yBottom: numY,
  };
};

const makeLabelBlock = (
  x: number,
  overlineFirstY: number,
  anchor: SankeyLabelAnchor,
  lines: string[],
  numStr: string,
  numColor: string,
): SankeyLabelBlock => {
  const { numY, yTop, yBottom } = verticalMetrics(overlineFirstY, lines.length);
  const { x0, x1 } = horizontalExtent(
    x,
    anchor,
    estimateLabelWidth(lines, numStr),
  );
  return {
    x,
    x0,
    x1,
    yTop,
    yBottom,
    anchor,
    overlineFirstY,
    numY,
    lines,
    numStr,
    numColor,
  };
};

const shiftLabelBlock = (block: SankeyLabelBlock, dy: number): void => {
  block.overlineFirstY += dy;
  block.numY += dy;
  block.yTop += dy;
  block.yBottom += dy;
};

const labelsOverlap = (a: SankeyLabelBlock, b: SankeyLabelBlock): number => {
  const overlapX = a.x1 > b.x0 && b.x1 > a.x0;
  if (!overlapX) {
    return 0;
  }
  const upper = a.yTop <= b.yTop ? a : b;
  const lower = upper === a ? b : a;
  return upper.yBottom + LABEL_MIN_GAP - lower.yTop;
};

const clampLabelBlock = (block: SankeyLabelBlock): void => {
  if (block.yTop < LABEL_VIEW_INSET) {
    shiftLabelBlock(block, LABEL_VIEW_INSET - block.yTop);
  }
  if (block.yBottom > HEIGHT - LABEL_VIEW_INSET) {
    shiftLabelBlock(block, HEIGHT - LABEL_VIEW_INSET - block.yBottom);
  }
};

const resolveSankeyLabelOverlaps = (blocks: SankeyLabelBlock[]): void => {
  for (let pass = 0; pass < 8; pass += 1) {
    let moved = false;
    const ordered = [...blocks].sort((left, right) => left.yTop - right.yTop);
    for (let i = 0; i < ordered.length; i += 1) {
      for (let j = i + 1; j < ordered.length; j += 1) {
        const overlap = labelsOverlap(ordered[i], ordered[j]);
        if (overlap <= 0) {
          continue;
        }
        const half = overlap / 2;
        shiftLabelBlock(ordered[i], -half);
        shiftLabelBlock(ordered[j], half);
        clampLabelBlock(ordered[i]);
        clampLabelBlock(ordered[j]);
        moved = true;
      }
    }
    if (!moved) {
      return;
    }
  }
};

const wrapOverline = (text: string, maxChars = MAX_OVERLINE_CHARS): string[] => {
  const words = text.split(' ');
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    if ((current + ' ' + word).trim().length > maxChars) {
      if (current) {
        lines.push(current);
      }
      current = word;
    } else {
      current = (current ? current + ' ' : '') + word;
    }
  }
  if (current) {
    lines.push(current);
  }
  return lines;
};

const toLayoutNodes = (nodes: ConnectionsBhSankeyNode[]): SankeyLayoutNode[] =>
  nodes.map(node => {
    const style = BH_SANKEY_NODE_STYLE[node.id] ?? {
      color: '#81CDC3',
      labelPos: 'right' as const,
    };
    return {
      ...node,
      color: style.color,
      labelPos: style.labelPos,
      label: node.label.toUpperCase(),
    };
  });

/**
 * Builds the BH connections Sankey SVG from snapshot nodes.
 * @param nodes - Warehouse or mock sankey node array.
 * @returns Inline SVG markup for the two-paths diagram.
 */
export const buildBhSankeySvgFromNodes = (
  nodes: ConnectionsBhSankeyNode[],
): string => {
  const layoutNodes = toLayoutNodes(nodes);
  const links = buildConnectionsBhSankeyLinks(nodes);
  const connectedCount =
    nodes.find(node => node.id === 'connected')?.value ?? 0;
  const nodeIndex = new Map(layoutNodes.map((node, index) => [node.id, index]));
  const sankeyGen = sankey<SankeyLayoutNode, SankeyLinkDatum>()
    .nodeWidth(10)
    .nodePadding(48)
    .nodeAlign(sankeyJustify)
    .extent([
      [PAD_LEFT, PAD_TOP],
      [WIDTH - PAD_RIGHT, HEIGHT - PAD_BOTTOM],
    ]);

  const graph = sankeyGen({
    nodes: layoutNodes.map(node => ({ ...node })),
    links: links.map(link => ({
      source: nodeIndex.get(link.source)!,
      target: nodeIndex.get(link.target)!,
      value: link.value,
      color: link.color,
    })),
  });

  const linkPath = sankeyLinkHorizontal<SankeyLayoutNode, SankeyLinkDatum>();

  const linksSvg = graph.links
    .map(link => {
      const path = linkPath(link);
      const strokeWidth = Math.max(1, link.width ?? 0);
      return `<path d="${path}" fill="none" stroke="${link.color}" stroke-opacity="0.45" stroke-width="${strokeWidth}"/>`;
    })
    .join('');

  const nodesSvg = graph.nodes
    .map(node => {
      const x = node.x0 ?? 0;
      const y = node.y0 ?? 0;
      const w = (node.x1 ?? 0) - x;
      const h = (node.y1 ?? 0) - y;
      return `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="2" fill="${node.color}"/>`;
    })
    .join('');

  const overlineAttrs = `font-family="Poppins, sans-serif" font-size="${OVERLINE_FONT}" font-weight="700" fill="${OVERLINE_FILL}" letter-spacing="0.1em"`;

  const labelBlocks = graph.nodes.map(node => {
    const lines = wrapOverline(node.label);
    const numStr = String(node.value);
    const numColor = node.color;
    const midY = ((node.y0 ?? 0) + (node.y1 ?? 0)) / 2;
    const totalH = lines.length * OVERLINE_LH + GAP + NUM_FONT;

    if (node.labelPos === 'left') {
      const x = (node.x0 ?? 0) - 14;
      const overlineFirstY = midY - totalH / 2 + OVERLINE_FONT;
      return makeLabelBlock(x, overlineFirstY, 'end', lines, numStr, numColor);
    }

    if (node.labelPos === 'right') {
      const x = (node.x1 ?? 0) + 14;
      const overlineFirstY = midY - totalH / 2 + OVERLINE_FONT;
      return makeLabelBlock(x, overlineFirstY, 'start', lines, numStr, numColor);
    }

    if (node.labelPos === 'above') {
      const x = node.x1 ?? 0;
      const numY = (node.y0 ?? 0) - 10;
      const overlineLastY = numY - NUM_FONT - GAP;
      const overlineFirstY = overlineLastY - (lines.length - 1) * OVERLINE_LH;
      return makeLabelBlock(x, overlineFirstY, 'start', lines, numStr, numColor);
    }

    const x = ((node.x0 ?? 0) + (node.x1 ?? 0)) / 2;
    const overlineFirstY = (node.y1 ?? 0) + 22 + OVERLINE_FONT;
    return makeLabelBlock(x, overlineFirstY, 'middle', lines, numStr, numColor);
  });

  resolveSankeyLabelOverlaps(labelBlocks);

  const labelsSvg = labelBlocks
    .map(block => {
      const numAttrs = `font-family="Lora, serif" font-size="${NUM_FONT}" font-weight="600" fill="${block.numColor}" letter-spacing="-0.01em"`;
      const overline = `<text text-anchor="${block.anchor}" ${overlineAttrs}>${block.lines
        .map(
          (line, index) =>
            `<tspan x="${block.x}" ${index === 0 ? `y="${block.overlineFirstY}"` : `dy="${OVERLINE_LH}"`}>${escapeXml(line)}</tspan>`,
        )
        .join('')}</text>`;
      return (
        overline +
        `<text x="${block.x}" y="${block.numY}" text-anchor="${block.anchor}" ${numAttrs}>${block.numStr}</text>`
      );
    })
    .join('');

  return `<svg viewBox="0 0 ${WIDTH} ${HEIGHT}" xmlns="http://www.w3.org/2000/svg" style="width:100%;height:auto;display:block;" role="img" aria-label="Sankey diagram showing flow of ${connectedCount} members connected to behavioral health care">${linksSvg}${nodesSvg}${labelsSvg}</svg>`;
};

/**
 * Builds the static Studio sample Sankey SVG for HTML export decor.
 * @returns Inline SVG markup using legacy mock node counts.
 */
export const buildBhSankeySvg = (): string =>
  buildBhSankeySvgFromNodes(BH_SANKEY_STATIC_NODES);
